import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  from,
  HttpLink,
  InMemoryCache,
  ServerError,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, Observable } from '@apollo/client/utilities';
import { Auth } from 'aws-amplify';

import WebSocketLink from './services/WebSocketLink';
import { mergeMessagesCache } from './utils/cache/messages';
import { mergeNotesCache } from './utils/cache/notes';
import { mergeNotificationsCache } from './utils/cache/notifications';

const wsLink = new WebSocketLink({
  url: process.env.REACT_APP_WEB_SOCKET_LINK,
  connectionParams: async () => {
    // simulate an auth token sent from the client over the WS connection
    const token = (await Auth.currentSession()).getIdToken().getJwtToken();
    return { ...(token && { token }) };
  },
});

const httpLink = new HttpLink({ uri: process.env.REACT_APP_GRAPHQL_URI });

// don't send queries unless authenticated
let sendQueries = false;

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription' && sendQueries;
  },
  wsLink,
  httpLink,
);

type handleErrorType = (message: string, code?: string) => any;

class AuthMiddlewareLink extends ApolloLink {
  request(operation, forward) {
    return new Observable((observer) => {
      let handle;
      void (async () => {
        try {
          // Use Amplify's Auth module to retrieve the token asynchronously
          const token = await Auth.currentSession().then((data) => data.getIdToken().getJwtToken());

          // add the authorization to the headers
          operation.setContext({
            headers: {
              authorization: token,
            },
          });

          // Proceed with the request chain
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        } catch (error) {
          // Handle any error that might occur during the token retrieval
          console.error('Error while fetching token:', error);
        }
      })();
      // Cleanup function to unsubscribe from the request chain
      return () => {
        if (handle) {
          handle.unsubscribe();
        }
      };
    });
  }
}

export const refreshToken = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const currentSession = user.signInUserSession;
    user.refreshSession(currentSession.refreshToken, (error) => {
      if (error) {
        console.error('Error while refreshing token:', error);
      }
    });
  } catch (error) {
    console.error('Error while refreshing token:', error);
  }
};

export const createErrorLink = (handleError: handleErrorType) => {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    graphQLErrors?.forEach(({ message, extensions }) => {
      handleError(message, extensions.code as string);
    });

    if ((networkError as ServerError)?.statusCode === 401) {
      const observable = new Observable<FetchResult<Record<string, any>>>((observer) => {
        void (async () => {
          await refreshToken();

          // Retry the failed request
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };

          forward(operation).subscribe(subscriber);
        })();
      });

      return observable;
    }

    return null;
  });
};

export const createClient = (onError: handleErrorType, isAuthenticated: boolean) => {
  const errorLink = createErrorLink(onError);
  const links = [errorLink];
  sendQueries = isAuthenticated;
  links.push(new AuthMiddlewareLink());
  links.push(link);

  return new ApolloClient({
    link: from(links),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            messages: {
              keyArgs: ['roomId'],
              merge: mergeMessagesCache,
            },
            organization: {
              merge: true,
            },
            notifications: {
              keyArgs: [],
              merge: mergeNotificationsCache,
            },
            notes: {
              keyArgs: ['listingId', 'tag'],
              merge: mergeNotesCache,
            },
            listingHistory: {
              keyArgs: ['listingId'],
              merge(existing = {}, incoming) {
                return { ...existing, ...incoming };
              },
            },
            listing: {
              merge: true,
            },
          },
        },
        Listing: {
          fields: {
            documents: {
              keyArgs: ['sort'],
              merge(existing, incoming, { args }) {
                if (args?.sort) {
                  return incoming;
                }
                return incoming || existing;
              },
            },
            expenses: {
              keyArgs: ['sort'],
              merge(existing, incoming, { args }) {
                if (args?.sort) {
                  return incoming;
                }
                return incoming || existing;
              },
            },
          },
        },
        Integration: {
          keyFields: false,
        },
        Room: {
          keyFields: ['id', 'listingId', 'type'],
        },
        VehiclePicture: {
          fields: {
            description: {
              read(description) {
                return description || '';
              },
              merge(description) {
                return description || '';
              },
            },
          },
        },
      },
    }),
    connectToDevTools: process.env.REACT_APP_APOLLO_DEVTOOLS === 'true',
  });
};
